import moment from "moment";
import PropTypes from "prop-types";
import { useEffect, useRef } from "react";

const TOP = `top`;
const BOTTOM = `bottom`;

const fixDPI = (val) => val * window.devicePixelRatio;

export const prettifier = function (n, ignoreTimezone = false) {
    const m = moment.unix(n / 1000);
    if (ignoreTimezone) {
        m.utc();
    }
    return m.format(`HH:mm:ss`);
};

const TimeGrid = ({
    seekCallback,
    timelineModel,
    displayLabels,
    primaryTickPosition,
    secondaryTickPosition,
    parentWidth,
    parentHeight,
    timelineWidth,
    preDraw,
    blocks
}) => {
    const ref = useRef(null);

    const ignoreTimezone = timelineModel.get(`ignoreTimezone`);
    const sectionStart = timelineModel.get(`sectionStart`);
    const sectionEnd = timelineModel.get(`sectionEnd`);

    const primaryScaleLabel = function (val) {
        return displayLabels ? prettifier(val, ignoreTimezone) : ``;
    };

    const secondaryScaleLabel = function () {
        return ``;
    };

    function setup(ctx) {
        // Define different tick types
        const scalesArr = [
            {
                first: timelineModel.primaryScaleFirst,
                next: timelineModel.primaryScaleNext,
                label: primaryScaleLabel,
                options: {
                    color: `#adb1bb`,
                    ...primaryTickPosition
                }
            },
            {
                first: timelineModel.secondaryScaleFirst,
                next: timelineModel.secondaryScaleNext,
                label: secondaryScaleLabel,
                options: {
                    color: `#5b6477`,
                    ...secondaryTickPosition
                }
            }
        ];

        // Label options
        if (displayLabels) {
            ctx.textAlign = `center`;
            if (displayLabels === `top`) {
                ctx.textBaseline = `bottom`;
            }
            if (displayLabels === `bottom`) {
                ctx.textBaseline = `top`;
            }
            ctx.font = `${fixDPI(11)}px Open Sans`;
            ctx.fillStyle = `#707788`;
        }

        // Line/tick stroke options
        ctx.strokeWidth = 1;
        ctx.lineCap = `square`;
        return scalesArr;
    }

    function draw(
        scalesArr,
        viewPortStartTime,
        viewPortEndTime,
        viewPortDuration,
        ctx
    ) {
        const labelMargin = fixDPI(5);
        // Start drawing ticks and labels
        scalesArr.forEach(
            ({ first, next, label, options: { top, bottom, color } }) => {
                let start,
                    end = first(viewPortStartTime, viewPortEndTime);
                do {
                    start = end;
                    end = next(start);

                    const ytop = Math.round(fixDPI(top));
                    const ybot = Math.round(fixDPI(bottom));

                    const x = Math.round(
                        fixDPI(
                            ((start - viewPortStartTime) / viewPortDuration) *
                                parentWidth
                        )
                    );

                    // Draw tick
                    ctx.strokeStyle = color;
                    ctx.beginPath();
                    ctx.moveTo(x, ytop);
                    ctx.lineTo(x, ybot);
                    ctx.stroke();
                    ctx.closePath();

                    // Draw label
                    if (displayLabels) {
                        ctx.fillStyle = color;
                        const y = Math.round(
                            displayLabels === `top`
                                ? ytop - labelMargin
                                : ybot + labelMargin
                        );
                        ctx.fillText(label(start), x, y);
                    }
                } while (end < viewPortEndTime && !isNaN(end));
            }
        );
    }

    useEffect(() => {
        const ctx = ref.current.getContext(`2d`);
        ctx.clearRect(0, 0, fixDPI(parentWidth), fixDPI(parentHeight));

        timelineModel.calculateIntervals();

        let viewPortStartTime = sectionStart;
        let viewPortEndTime = sectionEnd;

        // Adjust viewPortStartTime/max times for differently sized containers
        if (parentWidth > 0 && timelineWidth > 0) {
            const timelineDuration = sectionEnd - sectionStart;

            const rulerDuration =
                timelineDuration * (parentWidth / timelineWidth);

            const extraTime = (rulerDuration - timelineDuration) / 2;

            viewPortStartTime -= extraTime;
            viewPortEndTime += extraTime;
        }

        const viewPortDuration = viewPortEndTime - viewPortStartTime;
        const scalesArr = setup(ctx);

        // Reduce canvas blurriness
        ctx.translate(0.5, 0.5);

        preDraw(viewPortStartTime, viewPortEndTime, viewPortDuration, ctx);
        draw(
            scalesArr,
            viewPortStartTime,
            viewPortEndTime,
            viewPortDuration,
            ctx
        );

        // Reduce canvas blurriness
        ctx.translate(-0.5, -0.5);
    }, [
        timelineWidth,
        parentWidth,
        parentHeight,
        sectionStart,
        sectionEnd,
        ignoreTimezone,
        blocks
    ]);

    const onClick = ({ pageX }) => {
        const clickPosition = pageX - ref.current.getBoundingClientRect().left;
        const clickPercentage = clickPosition / parentWidth;

        let timeToSeek =
            sectionStart + clickPercentage * timelineModel.getSectionDuration();

        if (timeToSeek < 0) {
            timeToSeek = 0;
        }
        seekCallback(timeToSeek);
    };

    return (
        <canvas
            data-testid="timeGrid"
            width={fixDPI(parentWidth)}
            height={fixDPI(parentHeight)}
            style={{
                display: `block`,
                width: parentWidth,
                height: parentHeight
            }}
            onClick={onClick}
            ref={ref}
        />
    );
};

const TickPosition = PropTypes.shape({
    top: PropTypes.number.isRequired,
    bottom: PropTypes.number.isRequired
});

TimeGrid.propTypes = {
    seekCallback: PropTypes.func,
    timelineModel: PropTypes.object,
    displayLabels: PropTypes.oneOf([TOP, BOTTOM]),
    primaryTickPosition: TickPosition,
    secondaryTickPosition: TickPosition,
    parentWidth: PropTypes.number,
    parentHeight: PropTypes.number,
    timelineWidth: PropTypes.number,
    preDraw: PropTypes.func
};

TimeGrid.defaultProps = {
    seekCallback: () => {},
    parentHeight: 150,
    primaryTickPosition: {
        top: 30,
        bottom: 120
    },
    secondaryTickPosition: {
        top: 60,
        bottom: 90
    },
    preDraw: () => {}
};

export default TimeGrid;
